import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ListChecks, RefreshCw, ArrowRight, CheckCircle2, Square, CheckSquare } from "lucide-react";
import { useApi, errMsg } from "../lib/api";
import { Markdown } from "./Markdown";

const Item = ({ it, local, onTick }) => (
  <li className="flex items-start gap-2.5" data-testid={`todo-item-${it.id}`}>
    <button onClick={() => onTick(it)} className={`mt-0.5 shrink-0 ${it.done ? "text-[#00E599]" : "text-slate-500 hover:text-white"}`} aria-label={it.done ? "Mark not done" : "Mark done"} data-testid={`todo-tick-${it.id}`}>
      {it.done ? <CheckSquare size={16} /> : <Square size={16} />}</button>
    <div className={`min-w-0 ${it.done ? "line-through opacity-50" : ""}`}><Markdown text={(local && it.local) || it.text} /></div>
  </li>);

const Actions = ({ actions }) => !!actions?.length && (
  <div className="flex flex-wrap gap-2 mt-4" data-testid="todo-actions">
    {actions.map((a) => <Link key={a.key} to={a.path} data-testid={`todo-action-${a.key}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#00E599]/30 bg-[#00E599]/10 px-3 py-1.5 text-xs text-[#00E599] hover:bg-[#00E599]/20 transition-colors">
      {a.label}<ArrowRight size={12} /></Link>)}
  </div>);

export const ContractorTodoCard = () => {
  const api = useApi();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [local, setLocal] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(async (refresh) => {
    setBusy(true); setErr(null);
    try { setData((await api.get("/pe/my-todo", { params: { refresh: refresh || undefined } })).data); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  }, [api]);
  useEffect(() => { load(false); }, [load]);

  const tick = async (it) => {
    try { setData((await api.post(`/pe/my-todo/items/${it.id}`, { done: !it.done })).data); if (!it.done) toast.success("Marked done. Your records are re-checked when the list refreshes."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const items = data?.items || [];
  const done = items.filter((i) => i.done).length;
  const shown = items.filter((i) => showDone || !i.done);
  const txt = (p) => p && ((local && p.local) || p.text);

  return (
    <div className="c-surface p-5 mb-6 border-l-2 border-l-amber-400" data-testid="todo-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks size={16} className="text-amber-300 shrink-0" />
          <div className="min-w-0"><div className="text-sm font-semibold text-white">This week's to-do</div>
            {data && <div className="text-xs text-slate-400" data-testid="todo-meta">Week of {data.week}{items.length ? ` · ${done} of ${items.length} done` : ""}</div>}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {data?.language && <button onClick={() => setLocal(!local)} className="text-xs text-[#00E599] hover:underline" data-testid="todo-lang-toggle">{local ? "English" : data.language}</button>}
          <button onClick={() => load(true)} disabled={busy} title="Re-check your records and rewrite the list" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white disabled:opacity-50" data-testid="todo-refresh">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} />{busy ? "Checking…" : "Re-check"}</button>
        </div>
      </div>
      {err && <p className="text-sm text-red-400" data-testid="todo-error">{err}</p>}
      {busy && !data && <p className="text-xs text-slate-500" data-testid="todo-loading">Checking your filings, holds, workers and payroll…</p>}
      {data?.nothing && <p className="flex items-center gap-2 text-sm text-emerald-300" data-testid="todo-all-clear"><CheckCircle2 size={15} />Nothing to fix this week. Well done.</p>}
      {data && !data.nothing && <>
        {data.overview && <div className="mb-3"><Markdown text={txt(data.overview)} testid="todo-overview" /></div>}
        <ul className="grid gap-2.5" data-testid="todo-items">{shown.map((it) => <Item key={it.id} it={it} local={local} onTick={tick} />)}</ul>
        {items.length > 0 && done === items.length && <p className="mt-3 flex items-center gap-2 text-sm text-emerald-300" data-testid="todo-all-ticked"><CheckCircle2 size={15} />All items ticked. Press Re-check to confirm with your records.</p>}
        {done > 0 && <button onClick={() => setShowDone(!showDone)} className="mt-3 text-xs text-slate-400 hover:text-white underline" data-testid="todo-show-done">{showDone ? "Hide done items" : `Show ${done} done item(s)`}</button>}
        {data.next_step && done < items.length && <div className="mt-3 rounded-md bg-[#00E599]/5 border border-[#00E599]/20 px-3 py-2"><Markdown text={`**Next step:** ${txt(data.next_step)}`} testid="todo-next-step" /></div>}
        <Actions actions={data.actions} />
      </>}
    </div>);
};
